import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button, GridFrame, Label } from '../components/primitives';
import { FEDERAL_AUDITS, formatCaseDate } from '../data/evidence-cases';
import { PISCATAWAY } from '../data/seed';
import {
  readRestorable,
  restoredMeta,
  type RestorableReview,
} from './newReviewModel';
import './Landing.css';

const EXPLAINER = [
  {
    head: 'What you give it',
    body: 'A federal ESPC M&V plan or investment grade audit, as a PDF with real text '
      + 'in it. It stays in your browser.',
  },
  {
    head: 'What you get back',
    body: 'A list of findings, each quoting the exact clause, with either the arithmetic '
      + 'or the federal audit behind it — and a review comment sheet you can send to '
      + 'the contractor.',
  },
  {
    head: 'What it will not do',
    body: 'It does not score the plan, rank findings by severity, or write contract '
      + 'language for you. It points; you decide.',
  },
] as const;

/**
 * Reviews this browser is holding.
 *
 * Rendered only when there are any: a first visit must not carry an empty
 * container explaining that nothing is there yet. Separate from the page so
 * that both states can be asserted without a browser.
 */
export function YourReviews({ rows }: { rows: RestorableReview[] }) {
  if (rows.length === 0) return null;

  return (
    <>
      <div className="lp__section-head">
        <Label mono>03</Label>
        <h2 className="t-section">Your reviews</h2>
      </div>
      <ul className="lp__reviews">
        {rows.map((row) => (
          <li key={row.docId} className="lp__review">
            <Link className="lp__review-link" to={`/review/${row.docId}`}>
              <span className="lp__review-name">{row.fileName}</span>
              <span className="lp__review-meta">{restoredMeta(row)}</span>
            </Link>
          </li>
        ))}
      </ul>
      <p className="lp__reviews-note">
        Held in this browser and nowhere else.
      </p>
    </>
  );
}

export default function Landing() {
  const findingCount = PISCATAWAY.flags.length;
  const [reviews, setReviews] = useState<RestorableReview[]>([]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const rows = await readRestorable();
        if (!cancelled) setReviews(rows);
      } catch {
        // A browser with storage blocked has nothing to list, which is the
        // truth and not an error worth a panel of its own.
        if (!cancelled) setReviews([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="lp">
      {/* a) Headline, and the two things to do with it --------------------- */}
      <section className="lp__hero">
        <div className="lp__hero-headline">
          <Label mono>Pre-signature review · Federal ESPC</Label>
          <h1 className="t-display">Read the M&amp;V plan the way a dispute would.</h1>
        </div>
        <div className="lp__hero-sub">
          <p>
            Ceiling reads a federal ESPC measurement and verification plan and
            points at the clauses that have caused real disputes — quoting the
            clause, showing the arithmetic, and naming the federal audit where
            the same thing cost an agency money. It runs in your browser.
            Nothing is uploaded.
          </p>
          <div className="lp__hero-actions">
            <Button
              variant="primary"
              marker
              to={`/review/${PISCATAWAY.documentId}`}
            >
              Open the example review
            </Button>
            <Link className="lp__hero-secondary" to="/new">
              Review your own plan
            </Link>
          </div>
        </div>
      </section>

      {/* b) The proof block ------------------------------------------------ */}
      <section className="lp__proof">
        <div className="lp__proof-head">
          <div>
            <Label mono>Worked example</Label>
            <h2 className="t-display-2">
              Found in DOE&rsquo;s own model M&amp;V plan.
            </h2>
          </div>
          <div className="lp__proof-doc">
            <span>{PISCATAWAY.documentTitle}</span>
            <span>
              {PISCATAWAY.facility} · Delivery Order {PISCATAWAY.deliveryOrder}
            </span>
            <span>DOE/FEMP, February 2007 · {PISCATAWAY.preparedBy}</span>
          </div>
        </div>

        <div className="lp__tables">
          <div className="lp__table">
            <Label mono>Table 1-1 — project summary</Label>
            <p className="lp__figure">
              899,252 <span className="lp__figure-unit">kWh/yr</span>
            </p>
            <p className="lp__caption">
              EMCS electric energy savings, as stated in the summary table.
            </p>
          </div>

          <div className="lp__table">
            <Label mono>Table 4-4 — ECM detail, same measure</Label>
            <div className="lp__rows">
              <div className="lp__row">
                <span>Baseline</span>
                <span className="lp__row-value">1,266,002 kWh</span>
              </div>
              <div className="lp__row">
                <span>Post-installation</span>
                <span className="lp__row-value">811,894 kWh</span>
              </div>
            </div>
            <p className="lp__figure lp__figure--total">
              454,108 <span className="lp__figure-unit">kWh/yr</span>
            </p>
            <p className="lp__caption">
              Baseline minus post-installation, from the detail table for the
              same measure.
            </p>
          </div>
        </div>

        <div className="lp__delta">
          <span className="lp__delta-figure">&Delta; 445,144 kWh</span>
          <p className="lp__reconcile">
            The two tables disagree by 445,144 kWh. At the plan&rsquo;s own rate
            of $0.0525/kWh, the shared cost figure of $23,841 corresponds to
            454,108 kWh, not 899,252.
          </p>
        </div>

        <div className="lp__proof-foot">
          <span className="lp__mono-note">
            This is arithmetic. No model was involved, and you can redo it on
            paper from the plan itself.
          </span>
          <Link to={`/review/${PISCATAWAY.documentId}`}>
            Open this review — {findingCount} findings &rarr;
          </Link>
        </div>
      </section>

      {/* c) Scope, in the reviewer's terms ---------------------------------- */}
      <div className="lp__section-head">
        <Label mono>01</Label>
        <h2 className="t-section">What this is for</h2>
      </div>
      <div className="hgrid lp__explainer">
        {EXPLAINER.map(({ head, body }) => (
          <GridFrame key={head} pad="loose">
            <h3 className="lp__cell-head">{head}</h3>
            <p className="lp__cell-body">{body}</p>
          </GridFrame>
        ))}
      </div>

      {/* d) Evidence strip ------------------------------------------------- */}
      <div className="lp__section-head">
        <Label mono>02</Label>
        <h2 className="t-section">Every finding traces to one of these</h2>
      </div>
      <div className="hgrid lp__evidence">
        {FEDERAL_AUDITS.map((c) => (
          <GridFrame key={c.id} pad="loose">
            <a
              className="lp__case"
              href={c.url}
              target="_blank"
              rel="noreferrer noopener"
            >
              <Label>{c.issuer}</Label>
              <span className="lp__case-number">{c.reportNumber}</span>
              <span className="lp__case-meta">{formatCaseDate(c.date)}</span>
              <span className="lp__case-scale">{c.scale}</span>
            </a>
          </GridFrame>
        ))}
      </div>

      {/* e) Anything already under way in this browser --------------------- */}
      <YourReviews rows={reviews} />
    </div>
  );
}
